/**
 * Generated by scripts/generate-drizzle-schema.mjs. Do not edit by hand.
 * This product's inputs: scripts/drizzle-schema.config.mjs.
 * Regenerate: bash scripts/client-schema-fresh.sh (data-model-change skill).
 */

import { DrizzleAppSchema, type PowerSyncSQLiteDatabase } from "@powersync/drizzle-driver";
import { index, integer, sqliteTable, text } from "drizzle-orm/sqlite-core";
import type { InferInsertModel, InferSelectModel } from "drizzle-orm";

export const activity_recent = sqliteTable(
  "activity_recent",
  {
    id: text("id").primaryKey(),
    scope_id: text("scope_id"),
    list_id: text("list_id"),
    entity_type: text("entity_type"),
    entity_id: text("entity_id"),
    action: text("action"),
    actor_user_id: text("actor_user_id"),
    actor_name: text("actor_name"),
    actor_device_id: text("actor_device_id"),
    platform: text("platform"),
    server_ts: text("server_ts"),
    client_ts: text("client_ts"),
    patch: text("patch"),
    batch_id: text("batch_id"),
    reverts_activity_id: text("reverts_activity_id"),
  },
  (table) => [
    /** Feed: ORDER BY server_ts DESC, id DESC in getActivityFeedQuery */
    index("activity_recent_by_ts").on(table.server_ts, table.id),
    /** Entity history: WHERE entity_type = ? AND entity_id = ? in getEntityActivityQuery */
    index("activity_recent_by_entity").on(table.entity_type, table.entity_id, table.server_ts),
  ],
);

export const attachment = sqliteTable(
  "attachment",
  {
    id: text("id").primaryKey(),
    filename: text("filename"),
    content_type: text("content_type"),
    size: integer("size"),
    url: text("url"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
    user_id: text("user_id"),
    hash: text("hash"),
    entry_id: text("entry_id"),
  },
  (table) => [
    /** WHERE entry_id = ? ORDER BY created_at in getPhotosByEntryQuery */
    index("attachment_by_entry").on(table.entry_id, table.created_at),
    /** WHERE user_id = ? in getHasLocalDataQuery */
    index("attachment_by_user").on(table.user_id),
  ],
);

export const entry_version = sqliteTable(
  "entry_version",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    entry_id: text("entry_id"),
    entry_date: text("entry_date"),
    title: text("title"),
    body: text("body"),
    mood: integer("mood"),
    mood_tags: text("mood_tags"),
    word_count: integer("word_count"),
    client_ts: text("client_ts"),
    server_ts: text("server_ts"),
  },
  (table) => [
    /** WHERE entry_id = ? ORDER BY id DESC in getEntryVersionsQuery (id UUIDv7 = chronological order) */
    index("entry_version_by_entry").on(table.entry_id, table.id),
  ],
);

export const font_family = sqliteTable("font_family", {
  id: text("id").primaryKey(),
  family: text("family"),
  name: text("name"),
  category: text("category"),
  is_active: integer("is_active"),
  sort_order: integer("sort_order"),
  created_at: text("created_at"),
  updated_at: text("updated_at"),
});

export const font_variant = sqliteTable("font_variant", {
  id: text("id").primaryKey(),
  font_family_id: text("font_family_id"),
  weight: text("weight"),
  url: text("url"),
  created_at: text("created_at"),
  attachment_id: text("attachment_id"),
});

// Largest table in the product (1+ entries/day over years). Critical indexes.
export const journal_entry = sqliteTable(
  "journal_entry",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    entry_date: text("entry_date"),
    title: text("title"),
    body: text("body"),
    mood: integer("mood"),
    mood_tags: text("mood_tags"),
    is_favorite: integer("is_favorite"),
    template_key: text("template_key"),
    word_count: integer("word_count"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
    deleted_at: text("deleted_at"),
  },
  (table) => [
    /** WHERE user_id = ? AND entry_date = ?/range ORDER BY created_at DESC in today/calendar/on-this-day/streaks */
    index("journal_entry_by_user_date").on(table.user_id, table.entry_date, table.created_at),
    /** WHERE user_id = ? AND is_favorite = 1 ORDER BY entry_date DESC in favorites */
    index("journal_entry_by_favorite").on(table.user_id, table.is_favorite, table.entry_date),
  ],
);

// Bridge table entry<->tag.
export const journal_entry_tag = sqliteTable(
  "journal_entry_tag",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    entry_id: text("entry_id"),
    tag_id: text("tag_id"),
    created_at: text("created_at"),
  },
  (table) => [
    /** WHERE entry_id = ? in getEntryTagsQuery */
    index("journal_entry_tag_by_entry").on(table.entry_id),
    /** WHERE tag_id = ? in tag filter */
    index("journal_entry_tag_by_tag").on(table.tag_id),
  ],
);

// Small dataset per user — index by name for the picker.
export const journal_tag = sqliteTable(
  "journal_tag",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    name: text("name"),
    color: text("color"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
  },
  (table) => [
    /** WHERE user_id = ? ORDER BY name in getTagsQuery */
    index("journal_tag_by_user").on(table.user_id, table.name),
  ],
);

// Small dataset per user — indexes not needed currently.
export const member = sqliteTable("member", {
  id: text("id").primaryKey(),
  organization_id: text("organization_id"),
  user_id: text("user_id"),
  role: text("role"),
  created_at: text("created_at"),
});

// Small dataset — indexes not needed currently.
export const organization = sqliteTable("organization", {
  id: text("id").primaryKey(),
  name: text("name"),
  slug: text("slug"),
  logo: text("logo"),
  metadata: text("metadata"),
  created_at: text("created_at"),
});

// Active user sessions — small dataset, no indexes.
export const session = sqliteTable("session", {
  id: text("id").primaryKey(),
});

// Small global dataset (~20 themes) — indexes not needed.
export const themes = sqliteTable("themes", {
  id: text("id").primaryKey(),
  name: text("name"),
  background: text("background"),
  surface: text("surface"),
  accent: text("accent"),
  glow_colors: text("glow_colors"),
  created_at: text("created_at"),
  updated_at: text("updated_at"),
  is_dark: integer("is_dark"),
  foreground: text("foreground"),
  field_background: text("field_background"),
  danger: text("danger"),
  success: text("success"),
  warning: text("warning"),
  muted: text("muted"),
  pattern_type: text("pattern_type"),
  pattern_opacity: integer("pattern_opacity"),
  pair_id: text("pair_id"),
  font_normal: text("font_normal"),
  font_medium: text("font_medium"),
  font_semibold: text("font_semibold"),
  font_bold: text("font_bold"),
  radius: text("radius"),
  surface_foreground: text("surface_foreground"),
  surface_secondary: text("surface_secondary"),
  surface_secondary_foreground: text("surface_secondary_foreground"),
  surface_tertiary: text("surface_tertiary"),
  surface_tertiary_foreground: text("surface_tertiary_foreground"),
  overlay: text("overlay"),
  overlay_foreground: text("overlay_foreground"),
  default: text("default"),
  default_foreground: text("default_foreground"),
  accent_foreground: text("accent_foreground"),
  field_foreground: text("field_foreground"),
  field_placeholder: text("field_placeholder"),
  field_border: text("field_border"),
  danger_foreground: text("danger_foreground"),
  success_foreground: text("success_foreground"),
  warning_foreground: text("warning_foreground"),
  segment: text("segment"),
  segment_foreground: text("segment_foreground"),
  border: text("border"),
  focus: text("focus"),
  link: text("link"),
  surface_shadow: text("surface_shadow"),
  overlay_shadow: text("overlay_shadow"),
  field_shadow: text("field_shadow"),
  muted_foreground: text("muted_foreground"),
  divider: text("divider"),
  glass_background: text("glass_background"),
  glass_border: text("glass_border"),
  glass_highlight: text("glass_highlight"),
  is_active: integer("is_active"),
  sort_order: integer("sort_order"),
});

// Single row per user — indexes not needed.
export const user = sqliteTable("user", {
  id: text("id").primaryKey(),
  name: text("name"),
  email: text("email"),
  email_verified: integer("email_verified"),
  image: text("image"),
  created_at: text("created_at"),
  updated_at: text("updated_at"),
  locale: text("locale"),
  global_theme_id: text("global_theme_id"),
  is_blur: integer("is_blur"),
  font_family: text("font_family"),
  follow_device_theme: integer("follow_device_theme"),
  role: text("role"),
});

export const drizzleSchema = {
  activity_recent,
  attachment,
  entry_version,
  font_family,
  font_variant,
  journal_entry,
  journal_entry_tag,
  journal_tag,
  member,
  organization,
  session,
  themes,
  user,
};

export const AppSchema = new DrizzleAppSchema(drizzleSchema);

export type Database = (typeof AppSchema)["types"];

/**
 * Type helper for the Drizzle database instance wrapped with PowerSync.
 */
export type PowerSyncDrizzleDB = PowerSyncSQLiteDatabase<typeof drizzleSchema>;

export const USER_DATA_TABLES = [
  "attachment",
  "entry_version",
  "journal_entry",
  "journal_entry_tag",
  "journal_tag",
] as const;

export type ActivityRecent = InferSelectModel<typeof activity_recent>;
export type ActivityRecentInsert = InferInsertModel<typeof activity_recent>;
export type ActivityRecentRecord = ActivityRecent;
export type Attachment = InferSelectModel<typeof attachment>;
export type AttachmentInsert = InferInsertModel<typeof attachment>;
export type AttachmentRecord = Attachment;
export type EntryVersion = InferSelectModel<typeof entry_version>;
export type EntryVersionInsert = InferInsertModel<typeof entry_version>;
export type EntryVersionRecord = EntryVersion;
export type FontFamily = InferSelectModel<typeof font_family>;
export type FontFamilyInsert = InferInsertModel<typeof font_family>;
export type FontFamilyRecord = FontFamily;
export type FontVariant = InferSelectModel<typeof font_variant>;
export type FontVariantInsert = InferInsertModel<typeof font_variant>;
export type FontVariantRecord = FontVariant;
export type JournalEntry = InferSelectModel<typeof journal_entry>;
export type JournalEntryInsert = InferInsertModel<typeof journal_entry>;
export type JournalEntryRecord = JournalEntry;
export type JournalEntryTag = InferSelectModel<typeof journal_entry_tag>;
export type JournalEntryTagInsert = InferInsertModel<typeof journal_entry_tag>;
export type JournalEntryTagRecord = JournalEntryTag;
export type JournalTag = InferSelectModel<typeof journal_tag>;
export type JournalTagInsert = InferInsertModel<typeof journal_tag>;
export type JournalTagRecord = JournalTag;
export type Member = InferSelectModel<typeof member>;
export type MemberInsert = InferInsertModel<typeof member>;
export type MemberRecord = Member;
export type Organization = InferSelectModel<typeof organization>;
export type OrganizationInsert = InferInsertModel<typeof organization>;
export type OrganizationRecord = Organization;
export type Session = InferSelectModel<typeof session>;
export type SessionInsert = InferInsertModel<typeof session>;
export type SessionRecord = Session;
export type Themes = InferSelectModel<typeof themes>;
export type ThemesInsert = InferInsertModel<typeof themes>;
export type ThemesRecord = Themes;
export type User = InferSelectModel<typeof user>;
export type UserInsert = InferInsertModel<typeof user>;
export type UserRecord = User;
